import { useDevices } from '@api/hooks/useDevices';
import {
	usePushNotification,
	useSetPushNotification,
} from '@api/hooks/usePushNotification';
import { EmptyState } from '@components/EmptyState';
import { PageContainer } from '@components/PageContainer';
import {
	CardItemInner,
	DropdownPositions,
	Loader,
	Pagination,
	Typography,
} from '@gurupay/gp-ui-library';
import { usePagination } from '@hooks/usePagination.ts';
import { useTranslation } from 'react-i18next';

import { DeviceItem } from './DeviceItem';

export const PushNotifications = () => {
	const { t } = useTranslation('', { keyPrefix: 'pushNotifications' });
	const { data: pushSettings } = usePushNotification();
	const { mutate: setPushSettings } = useSetPushNotification();
	const enabled = pushSettings ? Object.values(pushSettings.types).every(Boolean) : false;
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();
	const { data, isLoading } = useDevices(currentPage, pageSize);
	const handleEnablePushNotifications = (e: React.ChangeEvent<HTMLInputElement>) =>
		setPushSettings({
			types: {
				payment_received: e.target.checked,
				payment_sent: e.target.checked,
				message_received: e.target.checked,
			},
		});
	const devices = data?.items ?? [];

	return (
		<PageContainer backUrl="/settings" title={t('title')}>
			<div className="max-w-[512px] m-auto space-y-space-32">
				<div>
					<p className="text-sm-medium text-neutral-600">{t('description')}</p>
					<CardItemInner
						actions={
							<label className="relative inline-flex cursor-pointer items-center">
								<input
									aria-label={t('enableLabel')}
									checked={enabled}
									className="peer sr-only"
									onChange={handleEnablePushNotifications}
									type="checkbox"
								/>
								<div className="h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-primary-600 peer-checked:after:translate-x-full" />
							</label>
						}
						className="!pb-0"
						title={t('enableLabel')}
					/>
				</div>
				<hr className="border-neutral-200" />
				<div>
					<Typography className="mb-space-16" level="h3">
						{t('registeredDevicesTitle')}
					</Typography>
					<p className="text-sm-medium text-neutral-600">
						{t('registeredDevicesDescription')}
					</p>
					{isLoading ? (
						<div className="flex justify-center items-center p-space-16">
							<Loader />
						</div>
					) : (
						devices.map((device) => <DeviceItem key={device.id} device={device} />)
					)}
					{devices.length === 0 && !isLoading && (
						<EmptyState subTitle={t('emptySubTitle')} title={t('emptyTitle')} />
					)}
					{!!data?.pagination?.count && (
						<Pagination
							currentPage={currentPage}
							dropdownOpenPosition={DropdownPositions.TOP}
							onPageChange={setCurrentPage}
							onPageSizeChange={handlePageSizeChange}
							pageOptions={pageOptions}
							pageSize={pageSize}
							pageText={pageText}
							totalCount={data.pagination.count}
							totalCountText={totalCountText}
						/>
					)}
				</div>
			</div>
		</PageContainer>
	);
};
